import { useState, type ReactElement } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { PageHeader } from '@/components/admin/AdminShell';
import { ListTable, type Column } from '@/components/admin/ListTable';
import { MetaBox } from '@/components/admin/MetaBox';
import { Notice } from '@/components/admin/Notice';
import {
  CSV_ACTION_LABELS,
  confirmOneOnOneCsv,
  confirmSuccessMeetingCsv,
  csvSampleUrl,
  uploadOneOnOneCsv,
  uploadSuccessMeetingCsv,
  type CsvJob,
  type CsvRowResult,
} from '@/features/csv/api';

export type MeetingPairImportKind = 'oneonone' | 'success-meeting';

const KIND_COPY: Record<
  MeetingPairImportKind,
  {
    title: string;
    description: string;
    counterpartLabel: string;
    sampleKind: 'oneonone' | 'success-meeting';
    listLabel: string;
    upload: (file: File) => Promise<CsvJob>;
    confirm: (jobId: string, reason: string) => Promise<CsvJob>;
  }
> = {
  oneonone: {
    title: '1on1を組み合わせCSVで一括作成',
    description:
      'お手元の「受講生 × 理事・講師」の組み合わせ表を取り込み、日時未定の1on1をまとめて作成します。日程はあとから受講生が候補日から選びます。通知予定は単件作成と同じく、日時が決まってから登録されます。',
    counterpartLabel: '理事・講師',
    sampleKind: 'oneonone',
    listLabel: '1on1一覧',
    upload: uploadOneOnOneCsv,
    confirm: confirmOneOnOneCsv,
  },
  'success-meeting': {
    title: 'サクセス面談を組み合わせCSVで一括作成',
    description:
      'お手元の「受講生 × サクセス担当」の組み合わせ表を取り込み、日時未定のサクセス面談をまとめて作成します。実施可能日（スケジュール枠）は空のままなので、担当が枠を入れたあと受講生が候補日から選びます。通知予定は単件作成と同じく、日時が決まってから登録されます。',
    counterpartLabel: 'サクセス担当',
    sampleKind: 'success-meeting',
    listLabel: 'サクセス面談一覧',
    upload: uploadSuccessMeetingCsv,
    confirm: confirmSuccessMeetingCsv,
  },
};

const COLUMNS = (counterpartLabel: string): Column<CsvRowResult>[] => [
  { key: 'row_no', header: '行', render: (row) => row.row_no },
  {
    key: 'member_id',
    header: '会員No.',
    render: (row) => row.member_id ?? '—',
  },
  {
    key: 'counterpart',
    header: counterpartLabel,
    render: (row) => (row.data.counterpart_email as string | undefined) ?? '—',
  },
  {
    key: 'title',
    header: '作成予定のタイトル',
    primary: true,
    render: (row) => (row.data.title as string | undefined) ?? '—',
  },
  { key: 'action', header: '判定', render: (row) => CSV_ACTION_LABELS[row.action] },
  {
    key: 'errors',
    header: 'エラー',
    render: (row) =>
      row.errors.length > 0 ? (
        <ul className="list-inside list-disc text-wpnotice-error">
          {row.errors.map((e) => (
            <li key={e}>{e}</li>
          ))}
        </ul>
      ) : (
        '—'
      ),
  },
];

/**
 * 面談組み合わせCSV一括作成（1on1=D-273 / サクセス面談=D-274）。
 * 日時は未定のまま作成し、候補日から受講生が確定する（D-183）。
 */
export function MeetingPairCsvImportPage({ kind }: { kind: MeetingPairImportKind }): ReactElement {
  const copy = KIND_COPY[kind];
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const typeId = searchParams.get('type');
  const [job, setJob] = useState<CsvJob | null>(null);
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function onFileSelected(file: File): Promise<void> {
    setBusy(true);
    setError(null);
    setJob(null);
    try {
      setJob(await copy.upload(file));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'アップロードに失敗しました。');
    } finally {
      setBusy(false);
    }
  }

  async function onConfirm(): Promise<void> {
    if (!job) return;
    setBusy(true);
    setError(null);
    try {
      setJob(await copy.confirm(job.id, reason));
    } catch (err) {
      setError(err instanceof Error ? err.message : '確定に失敗しました。');
    } finally {
      setBusy(false);
    }
  }

  const createCount = job?.summary?.create_count ?? 0;
  const errorCount = job?.summary?.error_count ?? 0;
  const isConfirmed = job?.status === 'confirmed';
  const canConfirm = createCount > 0;

  return (
    <>
      <PageHeader title={copy.title} description={copy.description} />

      {error && (
        <Notice kind="error" dismissible={false}>
          {error}
        </Notice>
      )}
      {isConfirmed && (
        <Notice kind="success" dismissible={false}>
          確定しました（新規作成 {job.summary?.create_count ?? 0} 件）。
          {errorCount > 0 ? ` エラー行 ${errorCount} 件は作成していません。` : null}
        </Notice>
      )}

      <MetaBox title="1. ファイルを選択">
        <p className="mb-2 text-wptext-muted">
          はじめての場合は
          <a href={csvSampleUrl(copy.sampleKind)} className="text-wpaccent hover:underline">
            サンプルCSVをダウンロード
          </a>
          して、会員No.と{copy.counterpartLabel}のログインメールを書き換えてください（登録済みの値が入っています）。
        </p>
        <input
          type="file"
          accept=".csv,text/csv"
          disabled={busy}
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) void onFileSelected(file);
          }}
        />
        <p className="mt-2 text-xs text-wptext-muted">
          列: 会員No.、相手のメール（{copy.counterpartLabel}のログインメール）。日時列はありません。
        </p>
      </MetaBox>

      {job && (
        <div className="mt-4">
          <MetaBox title="2. プレビュー">
            <dl className="mb-3 flex flex-wrap gap-x-6 gap-y-1 text-wptext-muted">
              <span>
                新規 <strong className="text-wptext-strong">{createCount}</strong>
              </span>
              <span>
                エラー
                <strong className="ml-1 text-wpnotice-error">{errorCount}</strong>
              </span>
            </dl>
            <ListTable<CsvRowResult>
              caption="組み合わせCSVプレビュー"
              rows={job.rows}
              columns={COLUMNS(copy.counterpartLabel)}
              rowKey={(row) => String(row.row_no)}
            />
          </MetaBox>

          {!isConfirmed && (
            <MetaBox title="3. 確定">
              {!canConfirm ? (
                <p className="text-wpnotice-error">
                  確定できる行がありません。ファイルを修正して再アップロードしてください。
                </p>
              ) : (
                <>
                  {errorCount > 0 && (
                    <p className="mb-3 text-wptext-muted">
                      エラー行はスキップし、正常な {createCount} 件だけ作成します。
                    </p>
                  )}
                  <label htmlFor="meeting-pair-csv-reason" className="mb-1.5 block text-wptext-muted">
                    確定理由（監査ログに記録されます）
                  </label>
                  <textarea
                    id="meeting-pair-csv-reason"
                    required
                    rows={2}
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    className="mb-3 w-full rounded-sm border border-wpborder px-3 py-2"
                  />
                  <button
                    type="button"
                    disabled={busy || !reason.trim()}
                    onClick={() => void onConfirm()}
                    className="rounded-sm border border-transparent bg-wpaccent px-3.5 py-2 text-white hover:bg-wpaccent-hover disabled:opacity-50"
                  >
                    確定する（{createCount}件）
                  </button>
                </>
              )}
            </MetaBox>
          )}
        </div>
      )}

      <button
        type="button"
        onClick={() =>
          navigate(typeId ? `/events?type=${encodeURIComponent(typeId)}` : '/events')
        }
        className="mt-4 text-wpaccent hover:underline"
      >
        ← {copy.listLabel}に戻る
      </button>
    </>
  );
}
